import MenuIcon from '@mui/icons-material/Menu';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LogoutIcon from '@mui/icons-material/Logout';
import {
  AppBar,
  Box,
  Button,
  Container,
  Drawer,
  IconButton,
  Toolbar,
  Tooltip,
  Typography,
  Menu,
  MenuItem,
  Divider,
  Avatar,
} from '@mui/material';
import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import MobileDrawer from './MobileDrawer';
import CartButton from '../Cart/CartButton';
import { ENABLE_CHECKOUT } from '../../config/featureFlags';

function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  const navItems = [
    { name: 'Products', path: '/products', dataTestid: "nav-products-button" },
    { name: 'Gallery', path: '/gallery', dataTestid: "nav-gallery-button" },
    { name: 'Wallet Designer', path: '/configurator', dataTestid: "nav-design-button" },
    { name: 'Reviews', path: '/reviews', dataTestid: "nav-reviews-button" },
    { name: 'Events', path: '/events', dataTestid: "nav-events-button" },
    { name: 'About', path: '/about', dataTestid: "nav-about-button" },
  ];

  // Social links moved to footer only

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleAccountMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleAccountMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    await logout();
    handleAccountMenuClose();
    navigate('/');
  };

  return (
    // Deep saddle from the brand tonal ramp — #8B4513 itself fails AA against the
    // #f4a261 active-link accent (3.4:1); this shade keeps white (12.2:1) and sand (5.9:1) passing.
    <AppBar position="sticky" sx={{ bgcolor: '#552a0e' }}>
      <Container>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            aria-label="menu"
            sx={{ mr: 2, display: { md: 'none' } }}
            onClick={handleDrawerToggle}
          >
            <MenuIcon />
          </IconButton>
          <Box sx={{ flexGrow: 1 }}>
            <Typography
              variant="h6"
              sx={{ cursor: 'pointer', display: { xs: 'none', sm: 'block' } }}
              onClick={() => navigate('/')}
              data-testid={'company-logo-header'}
            >
              KBros Leather
            </Typography>
          </Box>
          <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center' }}>
            {navItems.map((item) => (
              <Button
                key={item.name}
                sx={{
                  color: location.pathname.startsWith(item.path) && !item.external ? '#f4a261' : 'white',
                  fontWeight: location.pathname.startsWith(item.path) && !item.external ? 'bold' : 'normal',
                }}
                data-testid={item.dataTestid}
                onClick={() => {
                  if (item.external) {
                    window.open(item.path, '_blank', 'noopener,noreferrer'); // Open external links in new tab
                  } else {
                    navigate(item.path);
                  }
                }}
              >
                {item.name}
              </Button>
            ))}
            {ENABLE_CHECKOUT && (
            <Box sx={{ ml: 2, display: 'flex', alignItems: 'center' }}>
              {/* Account Button */}
              {isAuthenticated ? (
                <>
                  <Tooltip title="Account">
                    <IconButton
                      onClick={handleAccountMenuOpen}
                      sx={{ color: 'white', ml: 1 }}
                      data-testid="account-button"
                    >
                      <Avatar
                        sx={{
                          width: 32,
                          height: 32,
                          bgcolor: '#8B4513',
                          fontSize: '0.875rem'
                        }}
                      >
                        {user?.firstName?.charAt(0) || user?.email?.charAt(0).toUpperCase() || 'U'}
                      </Avatar>
                    </IconButton>
                  </Tooltip>
                  <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleAccountMenuClose}
                    PaperProps={{
                      sx: { minWidth: '200px' }
                    }}
                  >
                    <MenuItem
                      onClick={() => {
                        handleAccountMenuClose();
                        navigate('/account');
                      }}
                    >
                      <AccountCircleIcon sx={{ mr: 1, color: '#8B4513' }} />
                      My Account
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        handleAccountMenuClose();
                        navigate('/account/orders');
                      }}
                    >
                      <Box sx={{ width: 24, mr: 1 }} /> {/* Spacer for alignment */}
                      My Orders
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        handleAccountMenuClose();
                        navigate('/account/addresses');
                      }}
                    >
                      <Box sx={{ width: 24, mr: 1 }} /> {/* Spacer for alignment */}
                      My Addresses
                    </MenuItem>
                    <Divider />
                    <MenuItem onClick={handleLogout}>
                      <LogoutIcon sx={{ mr: 1, color: '#8B4513' }} />
                      Logout
                    </MenuItem>
                  </Menu>
                </>
              ) : (
                <Tooltip title="Login / Register">
                  <IconButton
                    onClick={() => navigate('/login')}
                    sx={{ color: 'white', ml: 1 }}
                    data-testid="login-button"
                  >
                    <AccountCircleIcon />
                  </IconButton>
                </Tooltip>
              )}
              <CartButton />
            </Box>
            )}
          </Box>
        </Toolbar>
      </Container>
      <Drawer
        anchor="left"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        sx={{ display: { md: 'none' } }}
      >
        <MobileDrawer
          navItems={navItems}
          location={location}
          navigate={navigate}
          onClose={handleDrawerToggle}
          isAuthenticated={isAuthenticated}
          user={user}
          logout={logout}
        />
      </Drawer>
    </AppBar>
  );
}

export default Navbar;
